import pt from 'prop-types';
import { Component, useMemo } from 'react';
import { createSafeContext, DateTimeLocalizationInfo } from '@mews/framework';
import { CalendarService, TimeService, WithChildren } from '@optimus-web/core';

interface CommanderContextValue {
    getCalendarService: () => CalendarService;
    getTimeService: () => TimeService;
    /** @deprecated this is only needed for legacy commanderForm */
    getDateTimeLocalizationInfo: () => DateTimeLocalizationInfo;
}

export type LegacyCommanderContextValue = CommanderContextValue;

const {
    rawContext: CommanderContext,
    CommanderContextProvider,
    useCommanderContext,
} = createSafeContext<'CommanderContext', CommanderContextValue>('CommanderContext');

export { CommanderContext };

interface CommanderContextProviderProps extends WithChildren {
    calendarService: CalendarService;
    timeService: TimeService;
    getDateTimeLocalizationInfo: () => DateTimeLocalizationInfo;
}

const ModernContextProvider: React.FC<CommanderContextProviderProps> = ({
    calendarService,
    timeService,
    getDateTimeLocalizationInfo,
    children,
}) => {
    const value = useMemo(
        () => ({
            getCalendarService: () => calendarService,
            getTimeService: () => timeService,
            getDateTimeLocalizationInfo,
        }),
        [calendarService, getDateTimeLocalizationInfo, timeService]
    );

    return <CommanderContextProvider value={value}>{children}</CommanderContextProvider>;
};

export const commanderContextProviderFactory = ({
    calendarService,
    timeService,
    getDateTimeLocalizationInfo,
}: CommanderContextProviderProps) =>
    class extends Component<WithChildren> {
        static childContextTypes = {
            getCalendarService: pt.func,
            getTimeService: pt.func,
            getDateTimeLocalizationInfo: pt.func,
        };

        getChildContext(): LegacyCommanderContextValue {
            return {
                getCalendarService: () => calendarService,
                getTimeService: () => timeService,
                getDateTimeLocalizationInfo,
            };
        }

        render() {
            return (
                <ModernContextProvider
                    calendarService={calendarService}
                    timeService={timeService}
                    getDateTimeLocalizationInfo={getDateTimeLocalizationInfo}
                >
                    {this.props.children}
                </ModernContextProvider>
            );
        }
    };

export const useCalendarService = () => useCommanderContext().getCalendarService();
export const useTimeService = () => useCommanderContext().getTimeService();
